// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ConditionalFormattingFieldsPopup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * @module ConditionalFormattingFieldsPopup popup component
 */

import React, { Component } from 'react';
import { observer } from 'mobx-react';
import connect from 'share/enhancers/Izenda.Connect';
import { find, isString } from 'lodash/fp';
import Popup from 'share/components/controls/CommonPopup/Izenda.Popup';
import GridLayout from 'share/components/controls/Izenda.GridLayout';
import ScrollBox from 'share/components/controls/Izenda.ScrollBox';
import InputByDataType from 'share/components/controls/Izenda.InputByDataType';
import Label  from 'share/components/controls/Izenda.Label';
import Button from 'share/components/controls/Izenda.Button';
import Field from './Izenda.ConditionalFormattingField';
import ConditionalFormattingAdditionalConditionsPopup from './Izenda.ConditionalFormattingAdditionalConditionsPopup';
import constants from 'logic/data/Izenda.ConditionalFormattingConstants';

const {GRID_STYLES, FIELD_CONTAINER_TYPES} = constants;

@observer
class ConditionalFormattingFieldsPopup extends Component {
  fields = [];

  constructor(props) {
    super(props);

    this.setFields();
  }

  setFields = () => {
    const {props: {gridProperties: {gridStyle}, fields: {simpleFields}, selectedFields}} = this;

    simpleFields.forEach(fieldItem => {
      const hasAdditionalConditions = gridStyle === GRID_STYLES.PIVOT && fieldItem.value.containerType === FIELD_CONTAINER_TYPES.VALUE;
      let field = find(selectedField => isString(selectedField) ? selectedField === fieldItem.name : selectedField.name === fieldItem.name, selectedFields);
      if(field){
        field = new Field(field, true, hasAdditionalConditions);
      }else{
        field = new Field(fieldItem.name, false, hasAdditionalConditions);
      }
      this.fields.push(field);
    });
  }

  getSelectedFields = () => {
    return this.fields.filter(field => field.isSelected).map(field => field.serializeData());
  }

  selectDeselectAll = (isSelected) => {
    const {fields} = this;
    fields.forEach((field) => { field.isSelected = isSelected });
  }

  renderField = (field, index) => {
    const {props: { localization, fields: {additionalFields}, openPopup }} = this;

    const onChange = (newValue) => {
      field.isSelected = newValue.value;
    }

    const onAdditionalConditionsChange = (additionalConditions) => {
      field.additionalConditions = additionalConditions;
    }

    const onEditClick = () => {
      openPopup(ConditionalFormattingAdditionalConditionsPopup, {
        isAdditionalConditionsForField: true, 
        field: field,
        additionalConditions: field.additionalConditions,
        additionalFields: additionalFields,
        onAdditionalConditionsChange: onAdditionalConditionsChange
      });
    }

    return (
      <GridLayout.Row key={index}>
        <GridLayout.Col cols={8}>
          <Label><InputByDataType value={field.isSelected} type={'checkbox'} onChange={onChange} />{field.name}</Label>
        </GridLayout.Col>
        <GridLayout.Col cols={4} className="u_textRight">
          {
            field.hasAdditionalConditions && (
              <a onClick={onEditClick}><span className="u_textUnderline">{localization.getText('CONDITIONAL_FORMATTING_LABEL_MORE')}</span></a>
            )
          }
        </GridLayout.Col>
      </GridLayout.Row>
    );
  }

  render() {
    const {
      selectDeselectAll, renderField, fields, getSelectedFields,
      props: { localization, onReject, onAccept, onFieldsChange }
    } = this;

    const selectAllFields = () => {
      selectDeselectAll(true);
    }

    const resetAllFields = () => {
      selectDeselectAll(false);
    }

    const onClosing = (callback, type) => {
      if(type === 'accept'){
        onFieldsChange(getSelectedFields());
        onAccept();
      }else{
        onReject();
      }
    };

    return (
      <Popup visible onClosing={onClosing} autoClose={false} type="none" sizeClass="modal-sm">
        <Popup.Header title={localization.getText('CONDITIONAL_FORMATTING_FIELDS_POPUP_TITLE')} />
        <Popup.Body>
        <div className="izenda-ConditionalFormattingFields">
            <GridLayout>
              <GridLayout.Row>
                <GridLayout.Col>
                  <Button onClick={selectAllFields} className="btn-report-default set-apart-btn" label={localization.getText('CONDITIONAL_FORMATTING_LABEL_SELECT_ALL_FIELDS')} />
                  <Button onClick={resetAllFields} className="btn-report-default" label={localization.getText('CONDITIONAL_FORMATTING_LABEL_RESET_ALL_FIELDS')} />
                </GridLayout.Col>
              </GridLayout.Row>
            </GridLayout>
            <div style={{'height': '150px', marginTop: '15px'}}>
              <ScrollBox fullWidth={true}>
                <GridLayout >
                  {fields.map(renderField)}
                </GridLayout>
              </ScrollBox>
            </div>
        </div>
        </Popup.Body>
        <Popup.Footer>
          <Popup.ConfirmationButton type="accept">{localization.getText('COMMON_LABEL_OK')}</Popup.ConfirmationButton>
          <Popup.ConfirmationButton type="cancel">{localization.getText('COMMON_LABEL_CANCEL')}</Popup.ConfirmationButton>
        </Popup.Footer>
      </Popup>
    );
  }
}

const popupWindow = connect()(ConditionalFormattingFieldsPopup);
popupWindow.displayName  = 'ConditionalFormattingFieldsPopup';
export default popupWindow;



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/PropertiesBuilder/ConditionalFormatting/Izenda.ConditionalFormattingFieldsPopup.jsx